/**
 * Water barriers (mission 6's frozen river and its variants): the shape of the bed that
 * `terrainFromDef` carves, and the height people actually stand at - the bed when wading,
 * the ice when it is frozen, the deck when they are on a bridge. Pure maths, no GPU objects;
 * the surface mesh that uses the same numbers is in riverSurface.ts.
 */

import { pondDepthAt } from '../../sim/statics';
import { HAZARDS } from '../../sim/tuning';
import type { MissionDef, PropDef, WaterBarrierDef } from '../../sim/types';
import { BRIDGE_DECK_Y, BRIDGE_HALF_WIDTH } from '../kit/pipeline';
import { smoothstep } from '../terrain/noise';

/** Height of the water / ice surface. Just off the pond's -0.28 so the two planes never z-fight. */
export const RIVER_LEVEL = -0.3;
/** Depth of the bed below the banks: mid-channel, and at a ford (ankle-deep under the surface). */
export const RIVER_BED = 0.9;
export const FORD_BED = 0.42;
/** The bank: the bed ramps from nothing at `half + BANK_OUT` to full depth at `half - BANK_IN`. */
export const BANK_OUT = 0.6;
export const BANK_IN = 0.8;
/**
 * Half-width of a crossing lane about each `crossings[]` x-position: the hazards module's own
 * figure, so ice drawn as sound is ice that holds (sim/hazards/terrain.ts `atCrossing`).
 */
export const CROSSING_HALF_WIDTH: number = HAZARDS.crossingHalfWidth;
export const CROSSING_FADE = 0.75;

/** Distance from the barrier's centre line. */
export function courseDistance(barrier: WaterBarrierDef, x: number, z: number): number {
  const pts = barrier.course.points;
  let best = Infinity;
  for (let i = 1; i < pts.length; i++) {
    const a = pts[i - 1];
    const b = pts[i];
    if (!a || !b) continue;
    const vx = b[0] - a[0];
    const vz = b[1] - a[1];
    const len2 = vx * vx + vz * vz;
    const t = len2 === 0 ? 0 : Math.min(1, Math.max(0, ((x - a[0]) * vx + (z - a[1]) * vz) / len2));
    const dx = x - (a[0] + vx * t);
    const dz = z - (a[1] + vz * t);
    best = Math.min(best, dx * dx + dz * dz);
  }
  return Math.sqrt(best);
}

/**
 * 1 inside a crossing lane, fading to 0 just outside it. The half-way point of the fade is the
 * lane's true edge, which is where the surface shader switches looks.
 */
export function crossingWeight(barrier: WaterBarrierDef, x: number): number {
  let w = 0;
  for (const cx of barrier.crossings) {
    const d = Math.abs(x - cx);
    w = Math.max(w, smoothstep(CROSSING_HALF_WIDTH + CROSSING_FADE, CROSSING_HALF_WIDTH - CROSSING_FADE, d));
  }
  return w;
}

/**
 * The crossings of a barrier that need a tell of their own on the water: every one that no bridge stands on.
 * Where a `bridge` prop covers the lane the DECK is the tell - riffles or sound ice drawn beside a deck read as
 * a second crossing next to the bridge (riverSurface.ts draws only these).
 */
export function openCrossings(barrier: WaterBarrierDef, props: readonly PropDef[]): number[] {
  const half = barrier.course.width / 2;
  const bridges = props.filter((p) => p.kind === 'bridge' && courseDistance(barrier, p.x, p.z) <= half);
  const slack = BRIDGE_HALF_WIDTH - CROSSING_HALF_WIDTH + 0.5;
  return barrier.crossings.filter((cx) => !bridges.some((b) => Math.abs(b.x - cx) <= slack));
}

/** How far the river bed lies below the surrounding ground at a point; 0 away from any barrier. */
export function riverDepthAt(def: MissionDef, x: number, z: number): number {
  let depth = 0;
  for (const barrier of def.waterBarriers) {
    const half = barrier.course.width / 2;
    const d = courseDistance(barrier, x, z);
    if (d >= half + BANK_OUT) continue;
    // Fords are shallows in open water. Thick ice needs no shelf under it: it is the ice you stand on.
    const ford = barrier.surface === 'water' ? crossingWeight(barrier, x) : 0;
    const bed = RIVER_BED + (FORD_BED - RIVER_BED) * ford;
    depth = Math.max(depth, bed * smoothstep(half + BANK_OUT, half - BANK_IN, d));
  }
  return depth;
}

/** 1 beside a barrier, 0 five metres clear: terrain relief is flattened so both banks sit level above the water. */
export function riverFlattening(def: MissionDef, x: number, z: number): number {
  let k = 0;
  for (const barrier of def.waterBarriers) {
    const half = barrier.course.width / 2;
    k = Math.max(k, smoothstep(half + 5, half + 1, courseDistance(barrier, x, z)));
  }
  return k;
}

/**
 * Is there LIQUID water over this point - a running river or a pond - for a hull to float in? Ice is
 * not: a barge on a frozen canal (mission 20 in the arctic) stands on the ice, not on the bed 0.65 m
 * under it, or the whole red boot-topping that says "boat" is buried and only its deck shows.
 */
export function openWaterAt(def: MissionDef, x: number, z: number): boolean {
  if (pondDepthAt(def, x, z) > 0.1) return true;
  for (const barrier of def.waterBarriers) {
    const near = courseDistance(barrier, x, z) < barrier.course.width / 2 + BANK_OUT;
    if (barrier.surface === 'water' && near) return true;
  }
  return false;
}

/**
 * What something dropped here lands ON, and whether it goes under: the water or the ice over a
 * barrier, else the ground itself. A bridge deck that has been blown off its piers needs both
 * (kit/spans.ts). `terrainY` is the bare ground at the point, for when there is no barrier at all.
 */
export function dropSurface(
  def: MissionDef, terrainY: number, x: number, z: number,
): { y: number; submerged: boolean } {
  for (const barrier of def.waterBarriers) {
    if (courseDistance(barrier, x, z) >= barrier.course.width / 2 + BANK_OUT) continue;
    return { y: RIVER_LEVEL, submerged: barrier.surface === 'water' };
  }
  return { y: terrainY, submerged: false };
}

/** Over a frozen barrier the ice is what carries you, whatever the bed does beneath it. */
function iceAt(def: MissionDef, x: number, z: number): boolean {
  for (const barrier of def.waterBarriers) {
    if (barrier.surface === 'ice' && courseDistance(barrier, x, z) < barrier.course.width / 2 + BANK_OUT) return true;
  }
  return false;
}

/** A bridge's walkable deck: an oriented rectangle at a fixed height. */
export interface Deck {
  x: number;
  z: number;
  cos: number;
  sin: number;
  halfLength: number;
  /** Height of the model's y = 0: the higher of the two banks it lands on. */
  baseY: number;
}

/** The decks of every bridge prop. A bridge runs along its local x, turned by yaw. */
export function bridgeDecks(props: readonly PropDef[], terrainY: (x: number, z: number) => number): Map<number, Deck> {
  const decks = new Map<number, Deck>();
  for (const prop of props) {
    if (prop.kind !== 'bridge') continue;
    const halfLength = (prop.length ?? 4) / 2;
    const cos = Math.cos(prop.yaw);
    const sin = Math.sin(prop.yaw);
    // Rotation about y carries local +x to (cos, -sin) in (x, z).
    const endA = terrainY(prop.x + cos * halfLength, prop.z - sin * halfLength);
    const endB = terrainY(prop.x - cos * halfLength, prop.z + sin * halfLength);
    decks.set(prop.id, { x: prop.x, z: prop.z, cos, sin, halfLength, baseY: Math.max(endA, endB) });
  }
  return decks;
}

/** Height a walker stands at: terrain, lifted to the ice over a frozen barrier and to the deck on a bridge. */
export function surfaceHeight(def: MissionDef, decks: Iterable<Deck>, terrainY: number, x: number, z: number): number {
  let y = terrainY;
  if (y < RIVER_LEVEL && iceAt(def, x, z)) y = RIVER_LEVEL;
  for (const deck of decks) {
    const dx = x - deck.x;
    const dz = z - deck.z;
    const along = dx * deck.cos - dz * deck.sin;
    const across = dx * deck.sin + dz * deck.cos;
    const onDeck = Math.abs(along) <= deck.halfLength && Math.abs(across) <= BRIDGE_HALF_WIDTH - 0.2;
    if (onDeck) y = Math.max(y, deck.baseY + BRIDGE_DECK_Y);
  }
  return y;
}
